import { DataGridTypes } from "devextreme-react/cjs/data-grid";
import { ClaimsSummaryAccumulator, DurationSummaryAccumulator } from "../types";
import { convertMinToDayHourMin } from "./convertMinToDayHourMin";

const isDurationSummaryRow = (value: unknown): value is {
  slaDuration: number | null;
  timeType?: string;
} => {
  return Boolean(value) && typeof value === 'object' && 'slaDuration' in (value as object);
};

const isClaimsSummaryRow = (value: unknown): value is {
  slaTitle?: string;
  eol?: string;
} => {
  return Boolean(value) && typeof value === 'object';
};

// SLA Duration handlers
const handleSlaDurationStart = (options: DataGridTypes.CustomSummaryInfo) => {
  options.totalValue = {
    totalMinutes: 0,
    totalRows: 0,
    timeType: 'S',
  } satisfies DurationSummaryAccumulator;
};

const handleSlaDurationCalculate = (options: DataGridTypes.CustomSummaryInfo) => {
  if (!isDurationSummaryRow(options.value)) return;

  const accumulator = options.totalValue as DurationSummaryAccumulator;
  const duration = Number(options.value.slaDuration);

  if (!Number.isFinite(duration)) return;

  accumulator.totalMinutes += duration;
  accumulator.totalRows += 1;
  accumulator.timeType = accumulator.totalRows === 1
    ? options.value.timeType || 'S'
    : accumulator.timeType;
};

const handleSlaDurationFinalize = (options: DataGridTypes.CustomSummaryInfo) => {
  const accumulator = options.totalValue as DurationSummaryAccumulator;

  if (!accumulator || accumulator.totalRows === 0) {
    options.totalValue = '0d 0h 0m';
    return;
  }

  options.totalValue = convertMinToDayHourMin(
    accumulator.totalMinutes / accumulator.totalRows,
    accumulator.timeType || 'S'
  );
};

const handleSlaDuration = (options: DataGridTypes.CustomSummaryInfo) => {
  switch (options.summaryProcess) {
    case 'start':
      handleSlaDurationStart(options);
      break;
    case 'calculate':
      handleSlaDurationCalculate(options);
      break;
    case 'finalize':
      handleSlaDurationFinalize(options);
      break;
  }
};

// Start DTG handlers
const handleStartDTGStart = (options: DataGridTypes.CustomSummaryInfo) => {
  options.totalValue = {
    totalRows: 0,
    totalPassed: 0,
  };
};

const handleStartDTGCalculate = (options: DataGridTypes.CustomSummaryInfo) => {
  if (!options.value) return;

  const accumulator = options.totalValue as { totalRows: number; totalPassed: number };
  accumulator.totalRows += 1;

  if (options.value.slaBreach === 0) {
    accumulator.totalPassed += 1;
  }
};

const handleStartDTGFinalize = (options: DataGridTypes.CustomSummaryInfo) => {
  const accumulator = options.totalValue as { totalRows: number; totalPassed: number };
  const percentageValue = accumulator?.totalRows > 0
    ? `${Number((accumulator.totalPassed / accumulator.totalRows) * 100).toFixed(2)}%`
    : '0.00%';
  options.totalValue = percentageValue;
};

const handleStartDTG = (options: DataGridTypes.CustomSummaryInfo) => {
  switch (options.summaryProcess) {
    case 'start':
      handleStartDTGStart(options);
      break;
    case 'calculate':
      handleStartDTGCalculate(options);
      break;
    case 'finalize':
      handleStartDTGFinalize(options);
      break;
  }
};

// Expected Data Value handlers
const getEOLCategoryForValue = (eol: string | undefined): keyof Omit<ClaimsSummaryAccumulator, 'totalRows' | 'bookinAccuracyRows'> | null => {
  switch (eol) {
    case 'OnTime':
      return 'onTime';
    case 'Late':
      return 'late';
    case 'Early':
      return 'early';
    case 'N/A':
      return 'notSet';
    default:
      return null;
  }
};

const handleExpectedDataValueStart = (options: DataGridTypes.CustomSummaryInfo) => {
  options.totalValue = {
    totalRows: 0,
    bookinAccuracyRows: 0,
    onTime: 0,
    late: 0,
    early: 0,
    notSet: 0,
  } satisfies ClaimsSummaryAccumulator;
};

const handleExpectedDataValueCalculate = (options: DataGridTypes.CustomSummaryInfo) => {
  if (!isClaimsSummaryRow(options.value)) return;

  const accumulator = options.totalValue as ClaimsSummaryAccumulator;
  accumulator.totalRows += 1;

  if (options.value.slaTitle?.includes('Bookin Accuracy')) {
    accumulator.bookinAccuracyRows += 1;
  }

  const category = getEOLCategoryForValue(options.value.eol);
  if (category) {
    accumulator[category] += 1;
  }
};

const handleExpectedDataValueFinalize = (options: DataGridTypes.CustomSummaryInfo) => {
  const accumulator = options.totalValue as ClaimsSummaryAccumulator;

  if (!accumulator || accumulator.totalRows === 0) {
    options.totalValue = {
      shouldDisplay: false,
      onTime: '0.0',
      late: '0.0',
      early: '0.0',
      notSet: '0.0',
    };
    return;
  }

  const toPercentage = (value: number) => ((value / accumulator.totalRows) * 100).toFixed(2);

  options.totalValue = {
    shouldDisplay: accumulator.bookinAccuracyRows > 0,
    onTime: toPercentage(accumulator.onTime),
    late: toPercentage(accumulator.late),
    early: toPercentage(accumulator.early),
    notSet: toPercentage(accumulator.notSet),
  };
};

const handleExpectedDataValue = (options: DataGridTypes.CustomSummaryInfo) => {
  switch (options.summaryProcess) {
    case 'start':
      handleExpectedDataValueStart(options);
      break;
    case 'calculate':
      handleExpectedDataValueCalculate(options);
      break;
    case 'finalize':
      handleExpectedDataValueFinalize(options);
      break;
  }
};

export const calculateSelectedRow = (
  options: DataGridTypes.CustomSummaryInfo,
) => {
  switch (options.name) {
    case 'slaDurationConverted':
      handleSlaDuration(options);
      break;
    case 'startDTG':
      handleStartDTG(options);
      break;
    case 'expectedDataValue':
      handleExpectedDataValue(options);
      break;
  }
};
